import { AllFulfillmentTypesType } from "@sellar-io/api-v2/types";
import { useDeliveryAreaGetBuyerAreas } from "../api/v2/deliveryArea/useDeliveryAreaGetBuyerAreas";
import { useRemoteFeatureFlags } from "../useRemoteFeatureFlags";
import { getDeliveryOptions } from "./getDeliveryOptions/getDeliveryOptions";

type DeliveryOptionsHookProps = {
  postcode?: string;
  hiddenOptions?: Array<
    Extract<
      AllFulfillmentTypesType,
      | "ups"
      | "apc"
      | "sellarDistribution"
      | "collection"
      | "direct"
      | "courier"
      | "dx"
      | "dxDropOff"
    >
  >;
  forceDeliveryAreaFetch?: boolean;
  // supplier side only parameter, ignored for buyer facing
  includeShadowRoutes?: boolean;
};

export const useDeliveryOptions = ({
  postcode,
  hiddenOptions = [],
  forceDeliveryAreaFetch,
  includeShadowRoutes = false,
}: DeliveryOptionsHookProps) => {
  const {
    isActive: isRemoteFeatureFlagActive,
    isLoading: featureFlagsAreLoading,
    featureFlags,
  } = useRemoteFeatureFlags();

  const isDistributionFlagEnabled = isRemoteFeatureFlagActive(
    "sellarDistributionBeta",
  );

  const {
    data: deliveryAreas,
    isFetched,
    isError: deliveryAreasHaveError,
  } = useDeliveryAreaGetBuyerAreas(
    {
      query: [
        {
          type: "RetailerPostcode",
          modifier: "EQUALS",
          value: postcode ?? "",
        },
      ],
      includeShadowRoutes,
    },
    {
      enabled: isDistributionFlagEnabled && !!postcode,
      staleTime: forceDeliveryAreaFetch ? undefined : 1000 * 60 * 10,
    },
  );

  const isBuyerInDistributionArea = !!deliveryAreas?.some(
    (area) => area.systemDefinedType === "sellarDistribution",
  );

  const requiresDeliveryAreas = isDistributionFlagEnabled && !!postcode;
  const isReady =
    featureFlags !== undefined &&
    (!requiresDeliveryAreas || deliveryAreas !== undefined);
  const isError =
    (!featureFlagsAreLoading && featureFlags === undefined) ||
    (requiresDeliveryAreas &&
      deliveryAreas === undefined &&
      deliveryAreasHaveError);

  const deliveryOptions = getDeliveryOptions({
    hiddenOptions,
    distribution: {
      isSupplierEnabled: isDistributionFlagEnabled,
      isBuyerInDistributionArea: isBuyerInDistributionArea,
    },
  });

  return {
    deliveryAreas,
    deliveryOptions,
    distributionEnabled: isDistributionFlagEnabled,
    isBuyerInDistributionArea,
    isFetched: !isDistributionFlagEnabled || !postcode ? true : isFetched,
    isReady,
    isError,
  };
};
